// Right-pane viewer + hand editor for a text artifact (#11523): a markdown
// or plain-text file the chat produced, fetched as text and saved back
// through the artifact content endpoint. Toolbar matches the FileViewer pane
// layout.
import React, { useEffect, useRef, useState } from "react";
import { Download, PencilLine, Check, Copy } from "lucide-react";
import { useLocation } from "react-router-dom";
import MarkdownRenderer from "../common/MarkDownRenderer";
import { useFlash } from "@/hooks/productTimerHooks";
import { fileExtension, kindForExt } from "../FpPages/Artifacts/artifactBadge";
import { useInvalidateArtifactVersions } from "./artifactVersions";
import ZoomControls from "../FpPages/RMS/Components/ZoomControls";
import { usePaneZoom } from "./usePaneZoom";
import fpFetch from "@/services/fpFetch";
import { saveText } from "./artifactDownload";
import { useViewerModeAction } from "../FpPages/ViewerModeToggle";
import { useArtifactPanelActions } from "./ArtifactPanelActions";
import ArtifactPaneHeader from "./ArtifactPaneHeader";
import ArtifactVersionHistoryPanel from "./ArtifactVersionHistoryPanel";
import { useArtifactHistory } from "./artifactHistory";
import {
  ArtifactSourcesButton,
  artifactCitationNumbers,
  artifactOriginLabel,
  useArtifactCitations,
  type ArtifactSourceMap,
} from "./ArtifactSources";

import {
  artifactVersionPreviewUrl,
  saveArtifactContent,
  type ArtifactVersion,
} from "../FpPages/Artifacts/useArtifacts";

interface TextArtifactPanelProps {
  // Null for a text file with no artifact row at hand (a chat-produced
  // .md opened straight from its turn, #11548 r23): the pane is read-only
  // — no hand-editing target, no version history — but still renders
  // markdown formatted.
  artifactId: string | null;
  /** False for a shared, not-mine artifact: cluster stays, pencil stands
   *  down (r60b). Defaults true. */
  editable?: boolean;
  title: string;
  filename: string;
  downloadUrl: string;
  onClose: () => void;
}

const isMarkdown = (filename: string): boolean =>
  /\.(md|markdown)$/i.test(filename);

const TextArtifactPanel: React.FC<TextArtifactPanelProps> = ({
  artifactId,
  editable = true,
  title,
  filename,
  downloadUrl,
  onClose,
}) => {
  const [text, setText] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const { zoomLevel, zoomIn, zoomOut, resetZoom } = usePaneZoom();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When set, the pane shows this past version's snapshot read-only.
  const [viewingVersion, setViewingVersion] = useState<{
    versionNumber: number;
    text: string;
    sourceMap: ArtifactSourceMap | null;
  } | null>(null);
  // The current version's citations (#11975): the markdown keeps its
  // markers, and the map is what renders them as chips.
  const citations = useArtifactCitations(artifactId, text);
  const shownSourceMap = viewingVersion
    ? viewingVersion.sourceMap
    : citations.sourceMap;
  const shownNumbers = artifactCitationNumbers(shownSourceMap);

  const { pathname } = useLocation();
  const [copied, flashCopied] = useFlash(2000);
  const invalidateVersions = useInvalidateArtifactVersions();
  const loadedOnceRef = useRef(false);
  const ext = fileExtension(filename) ?? "txt";

  const viewVersion = async (version: ArtifactVersion) => {
    if (artifactId === null) return;
    if (version.content !== null) {
      setViewingVersion({
        versionNumber: version.version_number,
        text: version.content,
        sourceMap: version.source_map ?? null,
      });
      return;
    }
    // Turn-produced snapshots of a text file live as S3 copies; fetch the
    // snapshot's text through the version preview endpoint.
    try {
      const response = await fpFetch(
        artifactVersionPreviewUrl(artifactId, version.version_number),
        { "x-finpilot-auth": true }
      );
      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
      setViewingVersion({
        versionNumber: version.version_number,
        text: await response.text(),
        sourceMap: version.source_map ?? null,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setEditing(false);
    setError(null);
    void (async () => {
      try {
        const response = await fpFetch(downloadUrl, {
          "x-finpilot-auth": true,
        });
        if (!response.ok) {
          throw new Error(`${response.status}: ${response.statusText}`);
        }
        const body = await response.text();
        if (!cancelled) {
          setText(body);
          // A re-read after the first is a chat edit landing (#11548 r44):
          // the versions list has grown by one. The first read is the mount.
          if (loadedOnceRef.current) invalidateVersions(artifactId);
          loadedOnceRef.current = true;
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [downloadUrl, artifactId, invalidateVersions]);

  const startEditing = () => {
    setDraft(text ?? "");
    setEditing(true);
  };

  const save = async () => {
    if (saving || artifactId === null) return;
    setSaving(true);
    setError(null);
    try {
      await saveArtifactContent(artifactId, { text: draft });
      setText(draft);
      setEditing(false);
      invalidateVersions(artifactId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // What the pane shows: the snapshot on screen, else the live text.
  const shownText = viewingVersion ? viewingVersion.text : text;

  const copy = () => {
    if (shownText === null) return;
    void navigator.clipboard.writeText(shownText).then(() => flashCopied());
  };

  // The history column's way of showing a version (#12481): a past
  // snapshot read-only, or the live text for null.
  const showVersion = (version: ArtifactVersion | null) => {
    setEditing(false);
    if (version === null) setViewingVersion(null);
    else void viewVersion(version);
  };

  const download = () => {
    if (text === null) return;
    saveText(text, filename);
  };

  // Version history presentation (#11819): the other header actions stand
  // down while it is open, and it renders as a column beside the text.
  const history = useArtifactHistory(artifactId);
  const { actions: artifactActions, dialogs: artifactDialogs } =
    useArtifactPanelActions({
      artifactId,
      title,
      file: {
        key: "download",
        label: "Download",
        icon: Download,
        onSelect: download,
      },
      copy: {
        key: "copy",
        label: "Copy",
        hint: copied ? "Copied!" : "Copy text",
        icon: Copy,
        disabled: shownText === null,
        onSelect: copy,
      },
      edit:
        editable && !viewingVersion && !editing
          ? {
              key: "edit",
              label: "Edit text",
              icon: PencilLine,
              disabled: text === null,
              onSelect: startEditing,
            }
          : undefined,
    });
  const viewerModeAction = useViewerModeAction(true);

  return (
    <div className="flex flex-col h-full">
      {/* One panel chrome for every artifact kind (#11548 r51/r60, #12481):
          the type tile and title (with the title's menu) on the left; the
          sources chip, the shared artifact actions, the layout toggle,
          Close on the right. The header folds as the pane narrows
          (#12091). */}
      <ArtifactPaneHeader
        title={title}
        badge={{ ext, kind: kindForExt(ext) }}
        origin={artifactOriginLabel(citations, pathname)}
        updatedAt={citations.updatedAt}
        version={
          artifactId !== null
            ? {
                viewing: viewingVersion?.versionNumber ?? null,
                onView: showVersion,
                historyOpen: history.open,
                onToggleHistory: history.toggle,
              }
            : undefined
        }
        sources={
          !editing ? (
            <ArtifactSourcesButton
              sourceMap={shownSourceMap}
              citationScope={citations.citationScope}
            />
          ) : undefined
        }
        onClose={onClose}
        leading={
          editing ? (
            <>
              {/* Editing gets explicit, labeled actions — a bare toolbar
                  icon was too easy to miss as the way to save. */}
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-[7px] border border-[var(--fp-border-strong)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--fp-ink)] hover:bg-[var(--fp-hover)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="flex items-center gap-1.5 rounded-[7px] bg-[var(--fp-ink)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--fp-surface)] hover:opacity-90 disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : undefined
        }
        aids={
          // Reading zoom (r74), in the header where it covers nothing
          // (#11819). Not while editing: the textarea manages its own type
          // size and CSS zoom would fight its height math.
          !editing && !history.open && text !== null && !error ? (
            <ZoomControls
              zoomLevel={zoomLevel}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onReset={resetZoom}
            />
          ) : undefined
        }
        actions={[
          ...artifactActions,
          ...(!history.open && viewerModeAction ? [viewerModeAction] : []),
        ]}
      >
        {artifactDialogs}
      </ArtifactPaneHeader>
      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          <div className={`h-full overflow-auto p-4 fp-scroll-bare`}>
            {error ? (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            ) : viewingVersion ? (
              <div style={{ zoom: zoomLevel / 100 }}>
                {isMarkdown(filename) ? (
                  <MarkdownRenderer
                    content={viewingVersion.text}
                    chatSourceMap={shownSourceMap ?? undefined}
                    citationNumbers={shownNumbers}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-[#10141B]">
                    {viewingVersion.text}
                  </pre>
                )}
              </div>
            ) : text === null ? (
              <div className="h-24 w-full animate-pulse rounded-md bg-gray-100" />
            ) : editing ? (
              <textarea
                aria-label="Artifact text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="h-full w-full resize-none rounded-md border border-gray-200 p-3 font-mono text-[13px] leading-relaxed focus:border-gray-400 focus:outline-none"
              />
            ) : (
              <div style={{ zoom: zoomLevel / 100 }}>
                {isMarkdown(filename) ? (
                  <MarkdownRenderer
                    content={text}
                    chatSourceMap={shownSourceMap ?? undefined}
                    citationNumbers={shownNumbers}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-[#10141B]">
                    {text}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
        {history.open && artifactId !== null && (
          <ArtifactVersionHistoryPanel
            artifactId={artifactId}
            viewing={viewingVersion?.versionNumber ?? null}
            onView={showVersion}
            onRestored={(version) => {
              setEditing(false);
              setViewingVersion(null);
              if (version.content !== null) {
                setText(version.content);
              } else {
                // A restored S3 snapshot: re-read the live file.
                void fpFetch(downloadUrl, { "x-finpilot-auth": true })
                  .then((r) => r.text())
                  .then(setText)
                  .catch(() => undefined);
              }
            }}
            onError={setError}
          />
        )}
      </div>
    </div>
  );
};

export default TextArtifactPanel;
